import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import Icon from './AppIcon';
import { A2P_CHARGE_AT_SUBMIT, A2P_CHARGE_CONFIRMED } from '../lib/billing-copy';
import { getAuthHeaders } from '../lib/api';
import { isActiveSubscription } from '../lib/subscription';
import SubscribeOnboarding from './SubscribeOnboarding';

import { AI_COMPLIANCE_OPT_IN_SUMMARY } from '../../lib/constants/ai-compliance-templates.js';

const MESSAGE_FLOW_SUMMARY = AI_COMPLIANCE_OPT_IN_SUMMARY;

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
];

const US_STATE_OPTIONS = [
  { value: 'AL', label: 'AL — Alabama' },
  { value: 'AK', label: 'AK — Alaska' },
  { value: 'AZ', label: 'AZ — Arizona' },
  { value: 'AR', label: 'AR — Arkansas' },
  { value: 'CA', label: 'CA — California' },
  { value: 'CO', label: 'CO — Colorado' },
  { value: 'CT', label: 'CT — Connecticut' },
  { value: 'DE', label: 'DE — Delaware' },
  { value: 'FL', label: 'FL — Florida' },
  { value: 'GA', label: 'GA — Georgia' },
  { value: 'HI', label: 'HI — Hawaii' },
  { value: 'ID', label: 'ID — Idaho' },
  { value: 'IL', label: 'IL — Illinois' },
  { value: 'IN', label: 'IN — Indiana' },
  { value: 'IA', label: 'IA — Iowa' },
  { value: 'KS', label: 'KS — Kansas' },
  { value: 'KY', label: 'KY — Kentucky' },
  { value: 'LA', label: 'LA — Louisiana' },
  { value: 'ME', label: 'ME — Maine' },
  { value: 'MD', label: 'MD — Maryland' },
  { value: 'MA', label: 'MA — Massachusetts' },
  { value: 'MI', label: 'MI — Michigan' },
  { value: 'MN', label: 'MN — Minnesota' },
  { value: 'MS', label: 'MS — Mississippi' },
  { value: 'MO', label: 'MO — Missouri' },
  { value: 'MT', label: 'MT — Montana' },
  { value: 'NE', label: 'NE — Nebraska' },
  { value: 'NV', label: 'NV — Nevada' },
  { value: 'NH', label: 'NH — New Hampshire' },
  { value: 'NJ', label: 'NJ — New Jersey' },
  { value: 'NM', label: 'NM — New Mexico' },
  { value: 'NY', label: 'NY — New York' },
  { value: 'NC', label: 'NC — North Carolina' },
  { value: 'ND', label: 'ND — North Dakota' },
  { value: 'OH', label: 'OH — Ohio' },
  { value: 'OK', label: 'OK — Oklahoma' },
  { value: 'OR', label: 'OR — Oregon' },
  { value: 'PA', label: 'PA — Pennsylvania' },
  { value: 'RI', label: 'RI — Rhode Island' },
  { value: 'SC', label: 'SC — South Carolina' },
  { value: 'SD', label: 'SD — South Dakota' },
  { value: 'TN', label: 'TN — Tennessee' },
  { value: 'TX', label: 'TX — Texas' },
  { value: 'UT', label: 'UT — Utah' },
  { value: 'VT', label: 'VT — Vermont' },
  { value: 'VA', label: 'VA — Virginia' },
  { value: 'WA', label: 'WA — Washington' },
  { value: 'WV', label: 'WV — West Virginia' },
  { value: 'WI', label: 'WI — Wisconsin' },
  { value: 'WY', label: 'WY — Wyoming' },
  { value: 'DC', label: 'DC — Washington D.C.' },
];

const CA_PROVINCE_OPTIONS = [
  { value: 'AB', label: 'AB — Alberta' },
  { value: 'BC', label: 'BC — British Columbia' },
  { value: 'MB', label: 'MB — Manitoba' },
  { value: 'NB', label: 'NB — New Brunswick' },
  { value: 'NL', label: 'NL — Newfoundland and Labrador' },
  { value: 'NS', label: 'NS — Nova Scotia' },
  { value: 'NT', label: 'NT — Northwest Territories' },
  { value: 'NU', label: 'NU — Nunavut' },
  { value: 'ON', label: 'ON — Ontario' },
  { value: 'PE', label: 'PE — Prince Edward Island' },
  { value: 'QC', label: 'QC — Quebec' },
  { value: 'SK', label: 'SK — Saskatchewan' },
  { value: 'YT', label: 'YT — Yukon' },
];

const BUSINESS_TYPE_ENTITY_OPTIONS = [
  { value: 'llc', label: 'LLC (Limited Liability Company)' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'cooperative', label: 'Co-operative' },
  { value: 'non_profit', label: 'Non-profit Corporation' },
];

const AIComplianceWidget = ({ onComplete, userId, compact = false, showSkip = false, checkoutContext }) => {
  const { user: authUser, userProfile, loading: authLoading, profileReady } = useAuth();
  const user = userId ? { id: userId } : authUser;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsOtp, setNeedsOtp] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [mobileDisplay, setMobileDisplay] = useState('');
  const [retryingCampaign, setRetryingCampaign] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [websiteCheck, setWebsiteCheck] = useState(null);
  const [websiteCheckLoading, setWebsiteCheckLoading] = useState(false);
  const [watching, setWatching] = useState(false);
  const [processingFailed, setProcessingFailed] = useState(false);
  const [resending, setResending] = useState(false);
  const resumedRef = useRef(false);

  const [form, setForm] = useState({
    businessType: 'sole_proprietor',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
    brandName: '',
    agreed: false,
    // Standard (LLC/EIN) only
    ein: '',
    businessTypeEntity: 'llc',
    businessTitle: 'Real Estate Agent',
    websiteUrl: '',
    confirmedTaxId: false,
  });

  const update = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'country') next.state = '';
      return next;
    });
    setError('');
  };

  const currentUserId = userId || authUser?.id;

  // Resume a sole-prop registration that is still processing or waiting on the OTP.
  useEffect(() => {
    if (resumedRef.current || !currentUserId || !profileReady) return undefined;
    if (!isActiveSubscription(userProfile?.subscription_status)) return undefined;
    resumedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/a2p/status?agentId=${encodeURIComponent(currentUserId)}`, { headers });
        const body = await res.json().catch(() => ({}));
        if (cancelled || !body?.data) return;
        const useCase = String(body.data.a2pUseCase || '').toUpperCase();
        if (useCase !== 'SOLE_PROPRIETOR') return;
        const brand = String(body.data.brandStatus || '').toUpperCase();
        const a2p = String(body.data.a2pStatus || '').toLowerCase();
        const identity = String(body.data.identityStatus || '').toUpperCase();
        if (identity === 'UNVERIFIED' || identity === 'SELF_DECLARED') {
          setNeedsOtp(true);
          setProcessingFailed(false);
          setError('');
          setStep(4);
          return;
        }
        if (brand === 'FAILED' || a2p === 'failed') {
          setError(body.data.failureReason || 'Registration failed before a verification code could be sent.');
          setProcessingFailed(true);
          setNeedsOtp(false);
          setStep(4);
          return;
        }
        if (['PENDING', 'IN_PROGRESS', 'IN_REVIEW'].includes(brand)) {
          setNeedsOtp(true);
          setProcessingFailed(false);
          setStep(4);
          return;
        }
        if (a2p === 'pending') {
          setNeedsOtp(false);
          setProcessingFailed(false);
          setWatching(true);
          setStep(4);
        }
      } catch {
        /* leave the form at step 1 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, profileReady, userProfile?.subscription_status]);

  // Poll until the sole-prop brand exists (OTP sent) or the registration fails.
  useEffect(() => {
    if (!watching || needsOtp || step !== 4 || !currentUserId) return undefined;
    let cancelled = false;
    const started = Date.now();
    const tick = async () => {
      if (cancelled) return;
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(
          `/api/a2p/status?agentId=${encodeURIComponent(currentUserId)}`,
          { headers }
        );
        const body = await res.json().catch(() => ({}));
        if (cancelled || !body?.data) return;
        const brand = String(body.data.brandStatus || '').toUpperCase();
        const a2p = String(body.data.a2pStatus || '').toLowerCase();
        const identity = String(body.data.identityStatus || '').toUpperCase();
        if (identity === 'UNVERIFIED' || identity === 'SELF_DECLARED') {
          setWatching(false);
          setProcessingFailed(false);
          setNeedsOtp(true);
          setError('');
          return;
        }
        if (brand === 'FAILED' || a2p === 'failed') {
          setWatching(false);
          setProcessingFailed(true);
          setError(body.data.failureReason || 'Registration failed before a verification code could be sent.');
          return;
        }
        if (['PENDING', 'IN_PROGRESS', 'IN_REVIEW'].includes(brand)) {
          setWatching(false);
          setProcessingFailed(false);
          setNeedsOtp(true);
          setError('');
          return;
        }
        if (brand === 'APPROVED') {
          setWatching(false);
          setStep(5);
          return;
        }
        if (Date.now() - started > 20 * 60 * 1000) {
          setWatching(false);
          setProcessingFailed(true);
          setError('Carrier review is taking longer than expected. You can retry, or leave this page and come back.');
        }
      } catch {
        /* keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [watching, needsOtp, step, currentUserId]);

  // Before submit, crawl-check the optional website. A site the user entered is always submitted.
  useEffect(() => {
    if (step !== 3 || form.businessType !== 'standard') {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setWebsiteCheckLoading(true);
      setWebsiteCheck(null);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/a2p/check-website', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agentId: currentUserId,
            businessName: form.brandName,
            websiteUrl: form.websiteUrl?.trim() || '',
            email: form.email?.trim() || '',
            ein: form.ein || '',
            country: form.country || 'US',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (!res.ok) {
            setWebsiteCheck({
              ok: false,
              message: data.error || 'Could not check website right now. We will decide at submit time.',
            });
          } else {
            setWebsiteCheck(data);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setWebsiteCheck({
            ok: false,
            message: err.message || 'Could not check website right now.',
          });
        }
      } finally {
        if (!cancelled) setWebsiteCheckLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, form.businessType, form.brandName, form.websiteUrl, form.email, currentUserId]);

  if ((authLoading && !userId) || (!profileReady && authUser)) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto" />
        <p className="text-muted-foreground mt-2">Loading...</p>
      </div>
    );
  }

  if (!user?.id) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <p className="text-destructive text-sm">Please sign in to complete AI Compliance.</p>
        </div>
      </div>
    );
  }

  const subscribed =
    profileReady && isActiveSubscription(userProfile?.subscription_status);

  if (profileReady && !subscribed) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-4 text-center px-6">
          <h2 className="text-xl font-bold text-foreground mb-2">Subscribe first</h2>
          <p className="text-sm text-muted-foreground">
            AI Compliance includes a one-time $20 carrier fee (brand + campaign). It is added as a separate line item on your Stripe invoice — not part of metered SMS usage.
          </p>
        </div>
        <SubscribeOnboarding checkoutContext={checkoutContext || undefined} />
      </div>
    );
  }

  const handleStart = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const payload = {
        agentId: currentUserId,
        businessType: form.businessType,
        street: form.street || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country,
      };
      if (form.businessType === 'standard') {
        Object.assign(payload, {
          businessName: form.brandName,
          ein: form.ein,
          businessTypeEntity: form.businessTypeEntity,
          // Blank = backend uses per-agent business page /p/{agentId}
          websiteUrl: form.websiteUrl?.trim() || undefined,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          businessTitle: form.businessTitle || 'Real Estate Agent',
        });
      } else {
        Object.assign(payload, {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone || undefined,
          mobile: form.mobile,
          brandName: form.brandName,
        });
      }
      const res = await fetch('/api/a2p/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error(res.ok ? 'Invalid response from server' : `Server error (${res.status}). Please try again.`);
      }
      if (!res.ok) throw new Error(data.error || 'Failed to start registration');
      const isStandard = form.businessType === 'standard';
      setMobileDisplay(form.mobile ? `${form.mobile.slice(-4).padStart(form.mobile.length, '*')}` : 'your mobile');
      setSuccessMessage(data.message || null);
      if (isStandard) {
        setStep(5);
        onComplete?.();
      } else if (data.brandSid && String(data.brandStatus || '').toUpperCase() !== 'FAILED') {
        setNeedsOtp(true);
        setProcessingFailed(false);
        setStep(4);
      } else if (data.processing) {
        setNeedsOtp(false);
        setProcessingFailed(false);
        setError('');
        setWatching(true);
        setStep(4);
      } else {
        setError(data.error || 'Registration did not start.');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      setError('Enter the code we sent to your phone');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/a2p/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ agentId: currentUserId, otpCode: otpCode.trim() }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error(res.ok ? 'Invalid response from server' : `Server error (${res.status}). Please try again.`);
      }
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      setStep(5);
    } catch (e) {
      setError(e.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!currentUserId) return;
    setResending(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/a2p/resend-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ agentId: currentUserId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not resend the code');
      setSuccessMessage(data.message || 'A new code was sent.');
    } catch (e) {
      setError(e.message || 'Could not resend the code');
    } finally {
      setResending(false);
    }
  };

  const handleRetryRegistration = async () => {
    if (!currentUserId) return;
    setLoading(true);
    setError('');
    setProcessingFailed(false);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/a2p/retry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ agentId: currentUserId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Retry failed');
      setNeedsOtp(false);
      setWatching(true);
      setStep(4);
    } catch (e) {
      setProcessingFailed(true);
      setError(e.message || 'Retry failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryCampaign = async () => {
    if (!currentUserId) return;
    setRetryingCampaign(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/a2p/retry-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ agentId: currentUserId }),
      });
      const data = await res.json();
      if (res.ok) {
        setStep(5);
        onComplete?.();
      } else {
        setError(data.error || 'Retry failed');
      }
    } catch (e) {
      setError(e.message || 'Retry failed');
    } finally {
      setRetryingCampaign(false);
    }
  };

  const handleDone = () => {
    onComplete?.();
  };

  const handleSkip = () => {
    onComplete?.();
  };

  const isStandard = form.businessType === 'standard';
  const canSubmitStep2SoleProp =
    form.firstName?.trim() &&
    form.lastName?.trim() &&
    form.email?.trim() &&
    form.mobile?.trim() &&
    form.brandName?.trim() &&
    form.street?.trim() &&
    form.city?.trim() &&
    form.state?.trim() &&
    form.postalCode?.trim() &&
    form.country?.trim();
  const canSubmitStep2Standard =
    form.brandName?.trim() &&
    form.ein?.trim() &&
    form.street?.trim() &&
    form.city?.trim() &&
    form.state?.trim() &&
    form.postalCode?.trim() &&
    form.country?.trim() &&
    form.firstName?.trim() &&
    form.lastName?.trim() &&
    form.email?.trim() &&
    form.phone?.trim();
  const canSubmitStep2 = isStandard ? canSubmitStep2Standard : canSubmitStep2SoleProp;
  const canSubmitConfirm =
    canSubmitStep2 &&
    form.agreed &&
    (!isStandard || form.confirmedTaxId);

  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Shield" size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2 text-center">Get approved to send messages</h2>
        <p className="text-muted-foreground mb-4 text-center">
          One quick form. We handle the rest. Carriers require this for AI-assisted messaging.
        </p>
        <p className="text-sm font-medium text-foreground mb-2">Do you have a registered business with a Tax ID?</p>
        <p className="text-xs text-muted-foreground text-center mb-3">US: EIN &nbsp;·&nbsp; Canada: CRA Business Number (BN)</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <Button
            variant={form.businessType === 'sole_proprietor' ? 'default' : 'outline'}
            onClick={() => update('businessType', 'sole_proprietor')}
            className="flex-1"
          >
            No — I am a sole proprietor
          </Button>
          <Button
            variant={form.businessType === 'standard' ? 'default' : 'outline'}
            onClick={() => update('businessType', 'standard')}
            className="flex-1"
          >
            Yes — I have an LLC, corporation, or Tax ID
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center mb-2">
          {form.businessType === 'sole_proprietor'
            ? 'No Tax ID needed. We\'ll send a quick verification code to your mobile.'
            : 'We\'ll register your business with carriers. No mobile code needed.'}
        </p>
        <p className="text-xs text-muted-foreground text-center mb-6">
          {A2P_CHARGE_AT_SUBMIT}
        </p>
        <div className={`flex gap-3 justify-center`}>
          <Button onClick={() => setStep(2)} className={showSkip ? 'flex-1 max-w-xs' : undefined}>
            Next
          </Button>
          {showSkip && (
            <Button variant="outline" onClick={handleSkip} className="flex-1 max-w-xs">
              Skip for Now
            </Button>
          )}
        </div>
        {showSkip && (
          <p className="text-xs text-muted-foreground text-center mt-4">
            You can complete AI Compliance later in Settings before sending messages.
          </p>
        )}
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          {isStandard ? 'Business and authorized representative' : 'Your information'}
        </h3>
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}
        <div className="space-y-4">
          {isStandard ? (
            <>
              {/* Country first so Canadians can set it before filling in tax ID and province */}
              <Select label="Country" options={COUNTRY_OPTIONS} value={form.country} onChange={(v) => update('country', v)} required />
              <Input
                label="Legal business name"
                value={form.brandName}
                onChange={(e) => update('brandName', e.target.value)}
                placeholder="Acme Realty LLC"
                required
              />
              <p className="text-xs text-muted-foreground -mt-2">
                {form.country === 'CA'
                  ? 'Must match your CRA registration letter character-for-character (Corp. vs Corporation, Ltd. vs Limited). Wrong legal name is the #1 brand rejection.'
                  : 'Must match your IRS EIN letter (CP 575 / 147c) character-for-character. Not a DBA, trade name, or invoice name unless that is the legal name on the letter.'}
              </p>
              {form.country === 'CA' ? (
                <div>
                  <Input
                    label="Business Number (CRA)"
                    value={form.ein}
                    onChange={(e) => update('ein', e.target.value.replace(/\D/g, '').slice(0, 9))}
                    placeholder="123456789"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">9-digit Business Number issued by the Canada Revenue Agency. Numbers only.</p>
                </div>
              ) : (
                <div>
                  <Input
                    label="EIN (Employer Identification Number)"
                    value={form.ein}
                    onChange={(e) => update('ein', e.target.value.replace(/\D/g, '').slice(0, 9))}
                    placeholder="12-3456789"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">Format: XX-XXXXXXX. Numbers only are OK.</p>
                </div>
              )}
              <Select
                label="Business type"
                options={BUSINESS_TYPE_ENTITY_OPTIONS}
                value={form.businessTypeEntity}
                onChange={(v) => update('businessTypeEntity', v)}
              />
              <p className="text-sm font-medium text-foreground pt-2">Business address</p>
              <Input
                label="Street address"
                value={form.street}
                onChange={(e) => update('street', e.target.value)}
                placeholder={form.country === 'CA' ? '102-386 St Paul St' : '123 Main St'}
                required
              />
              {form.country === 'CA' && (
                <p className="text-xs text-muted-foreground -mt-2">
                  Use the CRA-registered address. Put the unit first and use Canada Post abbreviations
                  (e.g. <span className="font-medium text-foreground">102-386 St Paul St</span>, not
                  &quot;386 St. Paul Street, Suite 102&quot;).
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Input label="City" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Los Angeles" required />
                <Select
                  label="State / Province"
                  options={form.country === 'CA' ? CA_PROVINCE_OPTIONS : US_STATE_OPTIONS}
                  value={form.state}
                  onChange={(v) => update('state', v)}
                  placeholder={form.country === 'CA' ? 'Select province' : 'Select state'}
                  required
                />
              </div>
              <Input label="Postal code" value={form.postalCode} onChange={(e) => update('postalCode', e.target.value)} placeholder={form.country === 'CA' ? 'A1A 1A1' : '90210'} required />
              <p className="text-sm font-medium text-foreground pt-2">Authorized representative</p>
              <div className="grid grid-cols-2 gap-4">
                <Input label="First name" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} placeholder="Jane" required />
                <Input label="Last name" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} placeholder="Doe" required />
              </div>
              <Input label="Email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@example.com" required />
              <div>
                <Input label="Phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+1 555 000 0000" required />
                <p className="text-xs text-muted-foreground mt-1">Include country code, e.g. +1 604 555 0123 for Canadian or US numbers.</p>
              </div>
              <Input
                label="Job title"
                value={form.businessTitle}
                onChange={(e) => update('businessTitle', e.target.value)}
                placeholder="Real Estate Agent"
              />
              <div>
                <Input
                  label="Business website (optional)"
                  value={form.websiteUrl}
                  onChange={(e) => update('websiteUrl', e.target.value)}
                  placeholder="https://yourrealty.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional, but recommended. We submit the website you enter — include https:// or we will add it.
                  Your legal business name should be visible on the page. Leave this blank only if you do not have a site.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input label="First name" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} placeholder="Jane" required />
                <Input label="Last name" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} placeholder="Doe" required />
              </div>
              <div>
                <Input label="Email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@example.com" required />
                <p className="text-xs text-muted-foreground mt-1">Use a permanent business or personal email — temporary or disposable addresses are rejected by carriers.</p>
              </div>
              <Input label="Phone (optional)" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+1 555 000 0000" />
              <div>
                <Input label="Mobile (for verification code)" value={form.mobile} onChange={(e) => update('mobile', e.target.value)} placeholder="+1 555 000 0000" required />
                <p className="text-xs text-muted-foreground mt-1">Must be a real US or Canadian mobile number — not a VoIP, Google Voice, or Twilio number. This number can only be used 3 times across all carrier registrations.</p>
              </div>
              <div>
                <Input label="Brand / business name" value={form.brandName} onChange={(e) => update('brandName', e.target.value)} placeholder="Your full name or DBA" required />
                <p className="text-xs text-muted-foreground mt-1">Use your legal name or DBA — exactly as it should appear to message recipients.</p>
              </div>
              <p className="text-sm font-medium text-foreground pt-2">Business address (required for carrier compliance)</p>
              {/* Country at the top of the address block so Canadians get the right province list */}
              <Select label="Country" options={COUNTRY_OPTIONS} value={form.country} onChange={(v) => update('country', v)} required />
              <Input
                label="Street address"
                value={form.street}
                onChange={(e) => update('street', e.target.value)}
                placeholder={form.country === 'CA' ? '102-386 St Paul St' : '123 Main St'}
                required
              />
              {form.country === 'CA' && (
                <p className="text-xs text-muted-foreground -mt-2">
                  Put the unit first with Canada Post abbreviations
                  (e.g. <span className="font-medium text-foreground">102-386 St Paul St</span>).
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Input label="City" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Los Angeles" required />
                <Select label="State / Province" options={form.country === 'CA' ? CA_PROVINCE_OPTIONS : US_STATE_OPTIONS} value={form.state} onChange={(v) => update('state', v)} placeholder={form.country === 'CA' ? 'Select province' : 'Select state'} required />
              </div>
              <Input label="Postal code" value={form.postalCode} onChange={(e) => update('postalCode', e.target.value)} placeholder={form.country === 'CA' ? 'A1A 1A1' : '90210'} required />
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
          {showSkip && (
            <Button variant="outline" onClick={handleSkip}>
              Skip for Now
            </Button>
          )}
          <Button onClick={() => setStep(3)} disabled={!canSubmitStep2}>
            Next — review & confirm
          </Button>
        </div>
      </div>
    );
  }

  if (step === 3) {
    const taxLabel = form.country === 'CA' ? 'Business Number (CRA)' : 'EIN';
    const taxHint =
      form.country === 'CA'
        ? 'Must match your CRA Business Number registration letter exactly. Corp. vs Corporation matters.'
        : 'Must match your IRS EIN letter (CP 575 / 147c) exactly. Not your W-2 or invoice name.';
    return (
      <div className="max-w-2xl mx-auto p-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Review before submitting</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Carrier review rejects mismatches. Double-check the details below before we submit.
        </p>
        <div className="rounded-lg border border-border bg-muted/30 p-3 mb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">What happens next</p>
          <p className="text-sm text-foreground">
            Brand review usually takes hours to a few days. Campaign vetting often takes{' '}
            <span className="font-medium">5–15 business days</span>. SMS sending stays locked until
            your campaign is verified — we will email you when it is ready.
          </p>
        </div>
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3 mb-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Legal business name</p>
            <p className="text-lg font-semibold text-foreground break-words">{form.brandName || '—'}</p>
            {isStandard && (
              <p className="text-xs text-muted-foreground mt-1">
                Compare this to your government registration letter before checking the box below.
              </p>
            )}
            {isStandard && websiteCheck?.legalNameWarning && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                {websiteCheck.legalNameWarning}
              </p>
            )}
          </div>
          {isStandard && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{taxLabel}</p>
              <p className="text-lg font-semibold text-foreground tracking-wide">
                {form.country === 'CA'
                  ? form.ein
                  : (form.ein?.length === 9 ? `${form.ein.slice(0, 2)}-${form.ein.slice(2)}` : form.ein)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{taxHint}</p>
              {websiteCheck?.taxIdWarning && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {websiteCheck.taxIdWarning}
                </p>
              )}
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Website for carrier verification</p>
            {websiteCheckLoading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                <Icon name="Loader2" size={14} className="animate-spin" />
                Checking if your website can be crawled…
              </p>
            ) : websiteCheck?.websiteUrl ? (
              <>
                <p className="text-sm text-foreground break-all mt-1">{websiteCheck.websiteUrl}</p>
                <p className={`text-xs mt-1 ${websiteCheck.usedFallback || websiteCheck.providedOk === false ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>
                  {websiteCheck.message}
                </p>
                {websiteCheck.emailWarning && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    {websiteCheck.emailWarning}
                  </p>
                )}
                {websiteCheck.emailNote && !websiteCheck.emailWarning && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {websiteCheck.emailNote}
                  </p>
                )}
                {websiteCheck.usedFallback && form.websiteUrl?.trim() && (
                  <p className="text-xs text-muted-foreground mt-1 break-all">
                    Entered site (not used): {form.websiteUrl.trim()}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-foreground break-all mt-1">
                {form.websiteUrl?.trim()
                  ? form.websiteUrl.trim()
                  : 'Your Nurture Buddy business page (auto)'}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Address</p>
            <p className="text-sm text-foreground">
              {[form.street, form.city, form.state, form.postalCode, form.country].filter(Boolean).join(', ')}
            </p>
            {form.country === 'CA' && (
              <p className="text-xs text-muted-foreground mt-1">
                Canada: unit first + abbreviations (e.g. 102-386 St Paul St) reduces address rejections.
              </p>
            )}
          </div>
        </div>
        {isStandard && (
          <label className="flex items-start gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={form.confirmedTaxId}
              onChange={(e) => update('confirmedTaxId', e.target.checked)}
              className="mt-1 rounded border-border"
            />
            <span className="text-sm text-foreground">
              I opened my government registration letter and confirmed the legal name and {taxLabel} match it exactly
              (including Corp./Corporation and punctuation).
            </span>
          </label>
        )}
        <label className="flex items-start gap-2 cursor-pointer mb-2">
          <input
            type="checkbox"
            checked={form.agreed}
            onChange={(e) => update('agreed', e.target.checked)}
            className="mt-1 rounded border-border"
          />
          <span className="text-sm text-foreground">
            I confirm the information is accurate and I have consent to message my contacts per the described opt-in flow.
          </span>
        </label>
        <p className="text-xs text-muted-foreground mb-4">{A2P_CHARGE_AT_SUBMIT}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
          <Button onClick={handleStart} loading={loading} disabled={!canSubmitConfirm}>
            {loading ? 'Submitting...' : 'Submit — we\'ll handle the rest'}
          </Button>
        </div>
      </div>
    );
  }

  if (step === 4 && !needsOtp) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {processingFailed ? 'Registration needs attention' : 'Submitting your registration'}
        </h3>
        <p className="text-muted-foreground text-sm mb-4">
          {processingFailed
            ? 'We could not send a verification code yet. Carrier review has to approve your profile before the text goes out.'
            : 'Carriers review your profile before they text a verification code. This usually takes a few minutes. Keep this page open.'}
        </p>
        {!processingFailed && (
          <p className="text-sm text-foreground flex items-center gap-2 mb-4">
            <Icon name="Loader2" size={16} className="animate-spin" />
            Waiting for carrier approval…
          </p>
        )}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          {processingFailed && (
            <Button variant="outline" onClick={() => { setStep(2); setError(''); setProcessingFailed(false); }}>
              Edit information
            </Button>
          )}
          {processingFailed && (
            <Button onClick={handleRetryRegistration} loading={loading}>
              Retry registration
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (step === 4 && needsOtp) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Enter verification code</h3>
        <p className="text-muted-foreground text-sm mb-4">
          We sent a 6-digit code to {mobileDisplay || 'your mobile'}. Enter it below to verify your mobile number with the carrier
          registry.
        </p>
        {successMessage && !error && (
          <p className="text-sm text-foreground mb-4">{successMessage}</p>
        )}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
            <p className="text-destructive text-sm">{error}</p>
            {error.toLowerCase().includes('brand') && error.toLowerCase().includes('processing') && (
              <Button
                onClick={handleRetryCampaign}
                disabled={retryingCampaign}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                {retryingCampaign ? 'Retrying...' : 'Retry campaign creation'}
              </Button>
            )}
          </div>
        )}
        <Input
          label="Verification code"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          maxLength={6}
          className="mb-4"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleResendOtp} loading={resending} disabled={loading}>
            Resend code
          </Button>
          <Button onClick={handleVerifyOtp} loading={loading} disabled={!otpCode.trim()}>
            Verify
          </Button>
        </div>
      </div>
    );
  }

  if (step === 5) {
    return (
      <div className="max-w-md mx-auto text-center p-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Successfully Submitted!</h2>
        <p className="text-muted-foreground mb-4">
          {successMessage ||
            'Your carrier registration has been submitted. Brand approval typically takes 1–2 business days; campaign approval up to 10–15 days. You\'ll receive an email once you\'re approved — no action needed.'}
        </p>
        <p className="text-sm text-foreground/80 bg-muted/50 rounded-lg px-4 py-3 mb-6">{A2P_CHARGE_CONFIRMED}</p>
        <Button onClick={handleDone} className="w-full">
          {compact ? 'Done' : 'Continue'}
        </Button>
      </div>
    );
  }

  return null;
};

export default AIComplianceWidget;
